import { Icon, IconButton, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useState, MouseEvent, useCallback } from 'react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import { ICellRendererParams } from 'ag-grid-community';
import { useSetAtom } from 'jotai';
import { cloneDeep } from 'lodash-es';
import { ConfirmDeletionDialog } from '../../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { FieldEntity, IField } from '../../../../data-models/field2.data-model';
import { MoreOptionsMenu, MenuItemWithDivider } from '../../../../components/MoreOptionsMenu/MoreOptionsMenu';
import { useCustomFieldsActions } from '../hooks/useCustomFieldsActions';
import {
  customFieldsOpenUploadModalState,
  customFieldsSelectedTemplateState,
} from '../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { CustomFieldFormInitialValuesState } from '../../forms2/CustomFieldFormStateJ';
import { CustomFieldForm2 } from '../../forms2/CustomFieldForm2';

export default function ActionsCellRenderer(params: ICellRendererParams<IField<unknown>>) {
  const { data } = params;
  const { colors } = useTheme();
  const { deleteField } = useCustomFieldsActions();
  const [openDeleteCFModal, setOpenDeleteCFModal] = useState(false);
  const [openEditCFModal, setOpenEditCFModal] = useState(false);

  const setFormInitialState = useSetAtom(CustomFieldFormInitialValuesState);

  const setCustomFieldsSelectedTemplate = useSetAtom(customFieldsSelectedTemplateState);
  const setCustomFieldsOpenUploadModal = useSetAtom(customFieldsOpenUploadModalState);

  const entity = params.data?.entity;
  const showUploadAction = entity === FieldEntity.company || entity === FieldEntity.fundCompany;

  const handleOnUploadBulk = () => {
    setCustomFieldsOpenUploadModal(true);
    setCustomFieldsSelectedTemplate(params.data?.entity as string);
  };

  const handleCloseDeleteModal = () => setOpenDeleteCFModal(false);

  const handleCloseEditModal = () => setOpenEditCFModal(false);

  const handleDeleteField = () => {
    if (!data) {
      return;
    }
    deleteField(data);
    handleCloseDeleteModal();
  };

  const handleEditField = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (data) {
        setFormInitialState(cloneDeep(data));
      }
      setOpenEditCFModal(true);
    },
    [data, setFormInitialState]
  );
  if (params?.node?.group) {
    return null;
  }

  return (
    <Stack
      display='grid'
      gridAutoFlow={'column'}
      alignItems='center'
      justifyItems={'center'}
      className='fields-menu'
      px={3}
    >
      {openEditCFModal && <CustomFieldForm2 onClose={handleCloseEditModal} editMode />}
      <ConfirmDeletionDialog
        open={openDeleteCFModal}
        onClose={handleCloseDeleteModal}
        onConfirm={() => handleDeleteField()}
        title={`Delete Custom Field "${data?.displayName}"?`}
      >
        <Typography variant='body2' color={'text.secondary'} sx={{ pt: '.25rem', minWidth: '30rem' }}>
          {' '}
          Deleting the field will permanently remove all data for this field from the system
        </Typography>
      </ConfirmDeletionDialog>
      {data?.timeSeries ? <TimelineIcon titleAccess='Supports time-based data' color='disabled' /> : <Icon />}
      {showUploadAction && (
        <IconButton color='secondary' size={'medium'} onClick={handleOnUploadBulk} title='upload'>
          <UploadIcon />
        </IconButton>
      )}
      {!openEditCFModal && !openDeleteCFModal && (
        <MoreOptionsMenu style={{ justifySelf: 'end' }}>
          <MenuItemWithDivider
            onClick={handleEditField}
            sx={{ display: 'flex', justifyItems: 'space-between', p: '.5rem' }}
          >
            <ModeOutlinedIcon sx={{ color: colors.neutral['60'], mr: '.25rem' }} />
            <Typography variant='body2' color={colors.primary[100]}>
              Edit
            </Typography>
          </MenuItemWithDivider>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setOpenDeleteCFModal(true);
            }}
            sx={{ display: 'flex', justifyItems: 'space-between', p: '.5rem' }}
          >
            <DeleteOutlinedIcon sx={{ color: colors.neutral['60'], mr: '.25rem' }} />
            <Typography variant='body2' color={colors.primary[100]}>
              Delete
            </Typography>
          </MenuItem>
        </MoreOptionsMenu>
      )}
    </Stack>
  );
}
